import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import { type ReactElement, useContext } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import usePlatform from '@/hooks/use-platform';
import DynamicT from '@/shared/components/DynamicT';
import NavBar from '@/shared/components/NavBar';
import PageMeta from '@/shared/components/PageMeta';
import { getBrandingLogoUrl, isLightLogoInDark, lightLogoBackdropClass } from '@/shared/utils/logo';

import { InlineNotification } from '../../components/Notification';
import ColumnFrame from '../ColumnFrame';

type Props = {
  readonly title: TFuncKey;
  /** Optional element rendered inline before the title (e.g. a channel badge). */
  readonly titleIcon?: ReactElement;
  readonly description?: TFuncKey | ReactElement | '';
  readonly titleProps?: Record<string, unknown>;
  readonly descriptionProps?: Record<string, unknown>;
  readonly notification?: TFuncKey;
  readonly onSkip?: () => Promise<void> | void;
  readonly onBack?: () => void;
  readonly isNavBarHidden?: boolean;
  readonly children: React.ReactNode;
};

const SecondaryPageLayout = ({
  title,
  titleIcon,
  description,
  titleProps,
  descriptionProps,
  notification,
  onSkip,
  onBack,
  isNavBarHidden,
  children,
}: Props) => {
  const { isMobile } = usePlatform();
  const { experienceSettings, theme } = useContext(PageContext);
  const logo =
    experienceSettings &&
    getBrandingLogoUrl({
      theme,
      branding: experienceSettings.branding,
      isDarkModeEnabled: experienceSettings.color.isDarkModeEnabled,
    });
  const navigate = useNavigateWithPreservedSearchParams();

  return (
    <div className="flex w-full flex-1 flex-col">
      <PageMeta titleKey={title} />
      <ColumnFrame slot="nav">
        <NavBar
          isHidden={isNavBarHidden}
          onSkip={onSkip}
          onBack={
            onBack ??
            (() => {
              navigate(-1);
            })
          }
        />
      </ColumnFrame>
      {isMobile && notification && <InlineNotification message={notification} className="mb-4" />}
      {/* Keeps the brand visible past the landing screen, where the user is asked to trust it. */}
      {logo && (
        <img
          data-brand-logo
          className={classNames(
            'mb-6 h-8 w-auto max-w-40 self-start object-contain',
            isLightLogoInDark(theme, experienceSettings.branding, logo) && lightLogoBackdropClass
          )}
          alt=""
          src={logo}
        />
      )}
      <div className="page-header mb-6">
        {/* Integration tests select `div[data-testid=page-title]`. */}
        <div data-testid="page-title" className="flex items-start gap-2">
          {/* One title line tall, so the icon stays on the first line of a wrapped title. */}
          {titleIcon && (
            <span className="page-title-icon flex h-7 shrink-0 items-center">{titleIcon}</span>
          )}
          <h1
            tabIndex={-1}
            className="page-title text-2xl/7 font-semibold tracking-tight text-foreground wrap-anywhere outline-none"
          >
            <DynamicT isNumberedWordKept forKey={title} interpolation={titleProps} />
          </h1>
        </div>
        {description && (
          <div className="page-description mt-2 text-sm text-pretty text-muted wrap-anywhere">
            {typeof description === 'string' ? (
              <DynamicT isNumberedWordKept forKey={description} interpolation={descriptionProps} />
            ) : (
              description
            )}
          </div>
        )}
      </div>
      {children}
      {!isMobile && notification && <InlineNotification message={notification} className="mt-6" />}
    </div>
  );
};

export default SecondaryPageLayout;
